/**
 * Replace the three encounter-code sections of a Draft.js raw template
 * with backend-provided bullet arrays.
 *
 * Boundaries (kept, content between gets swapped):
 *   - ICD-10 CODES WITH THE ENCOUNTER          icdhead   -> icdempty
 *   - CPT CODES WITH THE ENCOUNTER             cpthead   -> cptempty
 *   - CPT CODES WITH ASSOCIATED ICD-10 CODES   cpticdhead -> cpticdempty
 *     AND MODIFIERS
 *
 * If the anchors are missing entirely (legacy templates saved before
 * the three sections were added to the template), the sections are
 * INSERTED right before the next BOLD+UNDERLINE header following
 * `cofesda` / `cofeempty` (the empty/header for CLASSIFICATION OF
 * EVALUATION). That puts the new sections at the END of CLASSIFICATION's
 * content — the structural position they occupy in the new template.
 *
 * Mirrors backend `App\Helpers\EncounterCodeBullets::renderCodes`
 * and the TS sibling at faact_fe_v18/src/helpers/applyEncounterCodeBullets.ts
 * so PDF / editor / legacy-FE views all stay in sync.
 *
 * Accepts the template as either a JSON string (typical of `text_template`
 * coming back from the API) or an already-decoded raw object, and returns
 * the same shape it received. Returns the input unchanged when the
 * bullets payload is missing or no insertion anchor can be found either.
 */

const SECTIONS = [
  { start: "icdhead", end: "icdempty", field: "icd", headerText: "ICD-10 CODES WITH THE ENCOUNTER" },
  { start: "cpthead", end: "cptempty", field: "cpt", headerText: "CPT CODES WITH THE ENCOUNTER" },
  {
    start: "cpticdhead",
    end: "cpticdempty",
    field: "cpt_icd",
    headerText: "CPT CODES WITH ASSOCIATED ICD-10 CODES AND MODIFIERS",
  },
];

// Anchor used to locate the CLASSIFICATION OF EVALUATION section.
// Prefer the header block itself; fall back to its trailing empty.
const CLASSIFICATION_ANCHOR_KEYS = ["cofesda", "cofeempty"];

function headerBlock(def) {
  const len = def.headerText.length;
  return {
    key: def.start,
    text: def.headerText,
    type: "unstyled",
    depth: 0,
    inlineStyleRanges: [
      { offset: 0, length: len, style: "BOLD" },
      { offset: 0, length: len, style: "UNDERLINE" },
    ],
    entityRanges: [],
    data: {},
  };
}

function emptyBlock(key) {
  return {
    key,
    text: "",
    type: "unstyled",
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
  };
}

function replaceBetween(blocks, startIdx, endIdx, bullets) {
  return [...blocks.slice(0, startIdx + 1), ...bullets, ...blocks.slice(endIdx)];
}

function isSectionHeader(block) {
  if (!block.text || !block.text.trim()) return false;
  const ranges = block.inlineStyleRanges || [];
  let hasBold = false;
  let hasUnderline = false;
  for (const r of ranges) {
    if (r.style === "BOLD") hasBold = true;
    else if (r.style === "UNDERLINE") hasUnderline = true;
    if (hasBold && hasUnderline) return true;
  }
  return false;
}

/**
 * Find the index at which new sections should be inserted so they land
 * at the END of the CLASSIFICATION OF EVALUATION section. Returns:
 *   -1               when no CLASSIFICATION anchor exists (no-op)
 *   blocks.length    when CLASSIFICATION is the last section (append)
 *   otherwise        index of the next BOLD+UNDERLINE header after anchor
 */
function findInsertBeforeIdx(blocks) {
  let anchorIdx = -1;
  for (const key of CLASSIFICATION_ANCHOR_KEYS) {
    const idx = blocks.findIndex((b) => b.key === key);
    if (idx !== -1) {
      anchorIdx = idx;
      break;
    }
  }
  if (anchorIdx === -1) return -1;

  for (let i = anchorIdx + 1; i < blocks.length; i++) {
    if (isSectionHeader(blocks[i])) return i;
  }
  return blocks.length;
}

export default function applyEncounterCodeBullets(template, bullets) {
  if (!bullets) return template;
  const wasString = typeof template === "string";
  let parsed;
  try {
    parsed = wasString ? JSON.parse(template) : template;
  } catch (e) {
    return template;
  }
  if (!parsed || !parsed.blocks || !parsed.blocks.length) return template;

  let blocks = parsed.blocks;
  const missing = [];

  for (const def of SECTIONS) {
    const arr = bullets[def.field];
    if (!Array.isArray(arr) || !arr.length) continue;

    const startIdx = blocks.findIndex((b) => b.key === def.start);
    const endIdx = startIdx === -1 ? -1 : blocks.findIndex((b, i) => i > startIdx && b.key === def.end);

    if (startIdx !== -1 && endIdx !== -1) {
      blocks = replaceBetween(blocks, startIdx, endIdx, arr);
    } else {
      missing.push({ def, bullets: arr });
    }
  }

  if (missing.length > 0) {
    const insertIdx = findInsertBeforeIdx(blocks);
    if (insertIdx !== -1) {
      const insertion = [];
      for (const { def, bullets: bs } of missing) {
        insertion.push(headerBlock(def), ...bs, emptyBlock(def.end));
      }
      blocks = [...blocks.slice(0, insertIdx), ...insertion, ...blocks.slice(insertIdx)];
    }
  }

  if (blocks === parsed.blocks) return template;

  const out = { ...parsed, blocks };
  return wasString ? JSON.stringify(out) : out;
}
